import React, { useEffect, useState } from "react";
import { Grid, Box, Container } from "@mui/material";
import FoamOverviewCard from "./FoamOverviewCard";
import QualityTimelineCard from "../Lamination2/QualityTimelineCard";
import RollAnalyticsCard from "./RollAnalyticsCard";
import { type RollBucketsReport } from "../../../utils/rollBuckets";
// Inventory is hidden for now — restore this import with the card below.
// import InventoryOverviewCard from "./InventoryOverviewCard";
import ProductionLineDiagramCard, {
  LAM_COMPONENTS,
  FOAM_COMPONENTS,
} from "./ProductionLineDiagramCard";
import {
  type ComponentDetail,
  type ComponentState,
} from "./ProductionLineDiagram";
import {
  blenderState,
  butaneState,
  extruderState,
  coolingWaterState,
  highPressureState,
  foamWinderState,
  lamWinderState,
  lowPressureState,
  worst,
  EMPTY_FOAM_SETTINGS,
  BLENDER_RULES,
  FOAM_WINDER_RULES,
  LAM_WINDER_RULES,
  NOT_INSTRUMENTED_RULES,
  coolingWaterRules,
  extruderRules,
  highPressureRules,
  lowPressureRules,
} from "./statusMapping";
import { getBlendersCurrentStatus } from "../../../api/blender";
import type { ExtruderSettings } from "../../../api/extruder";
import { useExtruderThresholds } from "../../../utils/useExtruderThresholds";
import { getLam1CurrentStatus } from "../../../api/lam1";
import { getLam2CurrentStatus } from "../../../api/lam2";
import type { LamCurrentStatus } from "../../../api/lamLine";
import { getFoamLatest, getFoamWinderLatest } from "../../../api/foam";
import { getFoamSettings, type FoamSettings } from "../../../api/foamSettings";
import { WINDER_STALE_AFTER_S } from "../../common/FoamWinderStatus";

type DiagramState = Record<string, ComponentState>;
type DiagramDetails = Record<string, ComponentDetail>;

// ---- Formatting for the hover cards ----
/** Nothing to show. Not "0", not "off" — we simply have no value. */
const DASH = "\u2014";

/** "12s ago" / "3m ago" / "5h ago" / "2d ago" from a seconds count. */
const ago = (seconds: number): string => {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const num = (
  v: number | null | undefined,
  unit: string,
  digits = 1
): string => (v == null ? DASH : `${v.toFixed(digits)}${unit}`);

const bool = (
  v: boolean | null | undefined,
  yes: string,
  no: string
): string => (v == null ? DASH : v ? yes : no);

const titleCase = (v?: string | null): string =>
  v ? v.charAt(0).toUpperCase() + v.slice(1) : DASH;

/** Carriage position, however the line happens to report it. */
const carriage = (p?: boolean | string | null): string => {
  if (p == null) return DASH;
  if (typeof p === "string") return titleCase(p);
  return p ? "In" : "Out";
};

/**
 * Which heater zones are tripped. Takes either shape the rules do: 1-based
 * tripped indices, or the raw bit array.
 */
const faultZones = (f?: number[] | boolean[] | null): string => {
  if (f == null) return DASH;
  const zones = (f as (number | boolean)[])
    .map((v, i) => (typeof v === "boolean" ? (v ? i + 1 : 0) : Number(v)))
    .filter((n) => n > 0);
  return zones.length ? `Zone ${zones.join(", ")}` : "None";
};

/**
 * How current a reading is, for workers that write every poll — so silence is
 * genuinely staleness rather than a machine that simply has not changed.
 */
const readAt = (
  seconds?: number | null,
  stale?: boolean
): string | undefined => {
  const when = seconds == null ? null : ago(seconds);
  if (stale) return when ? `PLC unreachable \u2014 last read ${when}` : "PLC unreachable";
  return when ? `Read ${when}` : undefined;
};

/**
 * Same, for the blenders. plc-worker skips unchanged readings, so an old row
 * is a quiet machine rather than a missing one — worth wording differently so
 * nobody reads "4h" as a fault.
 */
const rowAge = (
  seconds?: number | null,
  stale?: boolean
): string | undefined => {
  const when = seconds == null ? null : ago(seconds);
  if (stale)
    return when ? `PLC unreachable \u2014 newest reading ${when}` : "PLC unreachable";
  return when ? `Newest reading ${when}` : undefined;
};

const blenderDetail = (b?: {
  status?: string;
  seconds_since?: number;
  stale?: boolean;
}): ComponentDetail => ({
  rules: BLENDER_RULES,
  readings: [{ label: "Status", value: titleCase(b?.status) }],
  freshness: rowAge(b?.seconds_since, b?.stale),
});

/**
 * How old a foam reading may be before it stops being evidence. The foam
 * worker polls every POLL_SECONDS (60 by default) and writes every cycle, so
 * this is five missed polls — long enough to ride out a blip, short enough
 * that a dead worker cannot sit there looking healthy.
 */
/** One blender's entry as /blender/currentStatus reports it. */
type BlenderStatus = { status?: string; seconds_since?: number; stale?: boolean };

/**
 * Diagram state and hover cards for one lamination line.
 *
 * Both lines are the same machinery reported by the same PLC program, so they
 * are wired identically and differ only in which two blenders feed them and
 * which /lam{n}/currentStatus they read. Keeping it in one place is what stops
 * the two diagrams from drifting apart — the densifier and winder decisions
 * below were made once for Lam 2 and apply verbatim to Lam 1.
 */
const lamLineDiagram = (
  l: LamCurrentStatus | undefined,
  blA: BlenderStatus | undefined,
  blB: BlenderStatus | undefined,
  thresholds: ExtruderSettings
): { state: DiagramState; details: DiagramDetails } => {
  const state: DiagramState = {
    blender_A: blenderState(blA),
    blender_B: blenderState(blB),
    extruder_A: extruderState(
      {
        speed: l?.screw_speed_a,
        position: l?.extruder_a_position,
        faults: l?.heater_fault_a_tripped,
        stale: l?.stale,
      },
      thresholds
    ),
    extruder_B: extruderState(
      {
        speed: l?.screw_speed_b,
        position: l?.extruder_b_position,
        // B's bits arrive as a raw array rather than tripped indices, and stay
        // null: neither line's PLC program has a "Heater Fault Bits" tag on
        // Extruder B.
        faults: l?.heater_fault_b,
        stale: l?.stale,
      },
      thresholds
    ),
    // Neither lamination densifier is instrumented yet: the column exists on
    // the reading but the PLC never fills it, so a color derived from it would
    // be a permanently-stopped red for a machine nobody is watching. Grey says
    // what is true — black artwork, a "No data" chip. Restore densifierState()
    // when the tag lands.
    densifier: "unknown",
    // Both lines have a winder, but the artwork has no winder overlay to paint.
    // The line element is the thing the winder drives — the web only moves
    // while it is running — so the line takes the winder's state directly
    // rather than rolling up the machines beside it. Give it an overlay of its
    // own in LAM_COMPONENTS if the PNGs ever land, and the line can go back to
    // being a roll-up.
    line: lamWinderState({ running: l?.winder_running, stale: l?.stale }),
  };

  // One PLC behind the whole line, so one heartbeat for every card.
  const read = readAt(l?.seconds_since_read, l?.stale);
  const details: DiagramDetails = {
    blender_A: blenderDetail(blA),
    blender_B: blenderDetail(blB),
    extruder_A: {
      rules: extruderRules(thresholds),
      readings: [
        { label: "Screw speed", value: num(l?.screw_speed_a, " Hz") },
        { label: "Carriage", value: carriage(l?.extruder_a_position) },
        { label: "Motor current", value: num(l?.motor_current_a, " A") },
        { label: "Heater faults", value: faultZones(l?.heater_fault_a_tripped) },
      ],
      freshness: read,
    },
    extruder_B: {
      rules: extruderRules(thresholds),
      readings: [
        { label: "Screw speed", value: num(l?.screw_speed_b, " Hz") },
        { label: "Carriage", value: carriage(l?.extruder_b_position) },
        { label: "Motor current", value: num(l?.motor_current_b, " A") },
        { label: "Heater faults", value: faultZones(l?.heater_fault_b) },
      ],
      freshness: read,
    },
    densifier: {
      rules: NOT_INSTRUMENTED_RULES,
      note: "The lamination densifiers are not wired up yet — the PLC has the column but never fills it.",
    },
    line: {
      rules: LAM_WINDER_RULES,
      note: "The line takes the winder's state: the web only moves while it is winding.",
      readings: [
        { label: "Winder", value: bool(l?.winder_running, "Running", "Stopped") },
      ],
      freshness: read,
    },
  };

  return { state, details };
};

const FOAM_STALE_AFTER_S = 300;

const MainOverviewPage: React.FC = () => {
  useEffect(() => {
    document.title = "Operations Overview";
  }, []);

  // Plant-wide extruder bands, the same values the extruder dashboards judge
  // run events by. The poll below re-runs when they land.
  const thresholds = useExtruderThresholds();
  const [foamLimits, setFoamLimits] = useState<FoamSettings>(EMPTY_FOAM_SETTINGS);
  const [lam1State, setLam1State] = useState<DiagramState>({});
  const [lam1Details, setLam1Details] = useState<DiagramDetails>({});
  const [lam2State, setLam2State] = useState<DiagramState>({});
  // What each component's hover card explains: the rules behind its color and
  // the reading they were applied to. Built alongside the state so the card can
  // never describe a color the diagram is no longer showing.
  const [lam2Details, setLam2Details] = useState<DiagramDetails>({});
  const [foamState, setFoamState] = useState<DiagramState>({});
  const [foamDetails, setFoamDetails] = useState<DiagramDetails>({});
  // First load only — the 60s poll refreshes in place without a spinner.
  const [statusLoading, setStatusLoading] = useState(true);

  // Each roll analytics card reports its buckets so both draw to the taller
  // bar rounded up to the next 100, and Lam 1 and Lam 2 compare across the
  // row. Their x axes are their own: each shows only the days it has data for.
  const [lam1Rolls, setLam1Rolls] = useState<RollBucketsReport | null>(null);
  const [lam2Rolls, setLam2Rolls] = useState<RollBucketsReport | null>(null);
  const rollsPeak = Math.max(
    0,
    ...[lam1Rolls, lam2Rolls].flatMap((r) => r?.buckets.map((b) => b.total) ?? [])
  );
  const rollsYMax = Math.max(100, Math.ceil(rollsPeak / 100) * 100);

  useEffect(() => {
    const controller = new AbortController();
    // Foam bands are nullable by design: if this fails, or nobody has filled
    // them in, the affected components stay grey rather than guessing.
    getFoamSettings(controller.signal)
      .then((j) => setFoamLimits({ ...EMPTY_FOAM_SETTINGS, ...j }))
      .catch(() => {
        /* leave every band unconfigured */
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchStatuses = async () => {
      const [blR, l1R, l2R, foamR, windR] = await Promise.allSettled([
        getBlendersCurrentStatus(signal),
        getLam1CurrentStatus(signal),
        getLam2CurrentStatus(signal),
        getFoamLatest(signal),
        getFoamWinderLatest(signal),
      ]);
      // Superseded: thresholds or foam limits landed and restarted this effect,
      // aborting us. Every request "settled" as a rejection, so carrying on
      // would drop the spinner and paint an all-black diagram until the real
      // fetch (/blender/currentStatus takes ~2.5s) finally colored it.
      if (signal.aborted) return;
      setStatusLoading(false);

      const bl =
        blR.status === "fulfilled"
          ? (blR.value as
              | Record<
                  string,
                  { status?: string; seconds_since?: number; stale?: boolean }
                >
              | undefined)
          : undefined;
      const l1 = l1R.status === "fulfilled" ? l1R.value : undefined;
      const l2 = l2R.status === "fulfilled" ? l2R.value : undefined;
      // One row per PLC; the foam line is the newest of them.
      const foamRows = foamR.status === "fulfilled" ? foamR.value ?? [] : [];
      const foam = foamRows
        .slice()
        .sort((a, b) => (b.ts_utc ?? "").localeCompare(a.ts_utc ?? ""))[0];
      const wind = windR.status === "fulfilled" ? windR.value ?? undefined : undefined;

      // ----- Lamination Line 1 (blenders 1 & 2; /lam1/currentStatus) -----
      // Line 1's extruder signals come from its own PLC now. They used to be
      // wired to the blender-1 PLC and read out of kgreading, which has
      // reported noise on those columns since the PLC was swapped.
      if (bl || l1) {
        const { state, details } = lamLineDiagram(l1, bl?.["1"], bl?.["2"], thresholds);
        setLam1State(state);
        setLam1Details(details);
      }

      // ----- Lamination Line 2 (blenders 3 & 4; /lam2/currentStatus) -----
      if (bl || l2) {
        const { state, details } = lamLineDiagram(l2, bl?.["3"], bl?.["4"], thresholds);
        setLam2State(state);
        setLam2Details(details);
      }

      // ----- Foam line (blender 5, the foam PLC, and the winder's own PLC) -----
      // Past the cutoff the last reading stops being evidence, so the parts it
      // feeds go grey rather than holding their final color.
      const foamFresh =
        foam && (foam.age_seconds ?? 0) <= FOAM_STALE_AFTER_S ? foam : undefined;
      setFoamState({
        blender: blenderState(bl?.["5"]),
        // No screw speed and no heater fault bits are collected for the foam
        // extruder yet, so there is nothing to color it from.
        extruder: "unknown",
        winder: foamWinderState(
          wind && {
            ...wind,
            stale: wind.age_seconds > WINDER_STALE_AFTER_S,
          }
        ),
        // The high pressure side is both its pressure band and the butane
        // on/off bit; whichever is worse is what the section is doing.
        butane_high_pressure_side: worst(
          highPressureState(foamFresh?.high_pressure_1, foamLimits),
          butaneState(foamFresh?.butane_control)
        ),
        butane_low_pressure_side: lowPressureState(
          foamFresh?.low_pressure,
          foamLimits
        ),
        // high_pressure_2 carries the cooling water temperature in °C, the
        // same signal the foam page charts as "Water Temperature".
        cooling_water: coolingWaterState(
          foamFresh?.high_pressure_2,
          foamLimits
        ),
      });

      // Cards show the last reading even once it is too old to color from —
      // "here is what we last saw, and here is how long ago" beats a blank.
      const foamAge = foam?.age_seconds ?? null;
      const foamRead =
        foamAge == null
          ? undefined
          : foamFresh
          ? `Read ${ago(foamAge)}`
          : `Last read ${ago(foamAge)} \u2014 too old to judge`;
      const winderRead =
        wind == null
          ? undefined
          : wind.age_seconds > WINDER_STALE_AFTER_S
          ? `Last read ${ago(wind.age_seconds)} \u2014 the winder PLC has gone quiet`
          : `Read ${ago(wind.age_seconds)}`;
      setFoamDetails({
        blender: blenderDetail(bl?.["5"]),
        extruder: {
          rules: NOT_INSTRUMENTED_RULES,
          note: "No screw speed or heater fault bits are collected for the foam extruder yet.",
        },
        winder: {
          rules: FOAM_WINDER_RULES,
          readings: [
            { label: "Winder", value: bool(wind?.status, "Running", "Stopped") },
            { label: "Mode", value: bool(wind?.auto_mode, "Auto", "Manual") },
          ],
          freshness: winderRead,
        },
        butane_high_pressure_side: {
          rules: highPressureRules(foamLimits),
          readings: [
            { label: "High side", value: num(foam?.high_pressure_1, " psi") },
            { label: "Butane", value: bool(foam?.butane_control, "On", "Off") },
          ],
          freshness: foamRead,
        },
        butane_low_pressure_side: {
          rules: lowPressureRules(foamLimits),
          readings: [
            { label: "Low side", value: num(foam?.low_pressure, " psi") },
          ],
          freshness: foamRead,
        },
        cooling_water: {
          rules: coolingWaterRules(foamLimits),
          readings: [
            // high_pressure_2 is the water temperature, despite the name.
            { label: "Water temperature", value: num(foam?.high_pressure_2, "\u00b0C") },
          ],
          freshness: foamRead,
        },
      });
    };

    fetchStatuses();
    const id = setInterval(fetchStatuses, 60_000);
    return () => {
      clearInterval(id);
      controller.abort();
    };
  }, [thresholds, foamLimits]);

  return (
    <Box sx={{ py: { xs: 1, sm: 2 } }}>
      <Container maxWidth="xl" disableGutters>
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
          {/* Leads with what each line is doing right now, one full-width strip
              per line; the shift-level cards below are the history. Lam 1 has
              roll data from 2026-09-22 on, when its PLC was commissioned. */}
          <Grid item xs={12}>
            <QualityTimelineCard
              title="Lamination 1 — Quality Timeline"
              titleVariant="h5"
              line="lam1"
            />
          </Grid>

          <Grid item xs={12}>
            <QualityTimelineCard
              title="Lamination 2 — Quality Timeline"
              titleVariant="h5"
              line="lam2"
            />
          </Grid>

          {/* Volume and the same rolls as a percentage mix, in one card off one
              set of shifts, so the two cannot describe different periods. The
              lines share a row on desktop and stack below lg, like the
              diagrams. */}
          <Grid item xs={12} lg={6}>
            <RollAnalyticsCard
              line="lam1"
              yMax={rollsYMax}
              onBuckets={setLam1Rolls}
            />
          </Grid>

          <Grid item xs={12} lg={6}>
            <RollAnalyticsCard
              line="lam2"
              yMax={rollsYMax}
              onBuckets={setLam2Rolls}
            />
          </Grid>

          {/* Inventory hidden for now — re-enable with its import above.
          <Grid item xs={12}>
            <InventoryOverviewCard />
          </Grid>
          */}

          {/* Both lamination diagrams share a row on desktop; they stack below
              lg, where half-width would squash the 1085x408 artwork. */}
          <Grid item xs={12} lg={6}>
            <ProductionLineDiagramCard
              title="Lamination Line 1 — Diagram"
              basePath="/diagrams/lam"
              components={LAM_COMPONENTS}
              state={lam1State}
              details={lam1Details}
              loading={statusLoading}
            />
          </Grid>

          <Grid item xs={12} lg={6}>
            <ProductionLineDiagramCard
              title="Lamination Line 2 — Diagram"
              basePath="/diagrams/lam"
              components={LAM_COMPONENTS}
              state={lam2State}
              details={lam2Details}
              loading={statusLoading}
            />
          </Grid>

          {/* Foam snapshot beside its diagram, matching the lamination pair. */}
          <Grid item xs={12} lg={6}>
            <FoamOverviewCard />
          </Grid>

          <Grid item xs={12} lg={6}>
            <ProductionLineDiagramCard
              title="Foam Line — Diagram"
              basePath="/diagrams/foam"
              components={FOAM_COMPONENTS}
              state={foamState}
              details={foamDetails}
              loading={statusLoading}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default MainOverviewPage;
